import { connect } from 'react-redux';
import { useState, useEffect, } from 'react';

import { 
  SET_EDIT_PROJECT_DIALOGUE_ID, SiteboardApiValidator, ADD_PROJECT, SET_PATH, SET_PREFERS_PROJECTS_LIST_VIEW, SET_NEW_PROJECT_DIALOGUE_SHOWING, 
    DateStringMMDDYYYY,
} from '../actions/types';
import { siteboardAction } from '../actions/defaultActions';
import { keyCountInObject, } from '../Util';

import PoweredByBar from './PoweredByBar';
import ProjectTile from './ProjectTile';
import PushButton from './PushButton';
import GridListToggle from './GridListToggle';
import SortableTable from './SortableTable';

// icon imports
import {
  FolderPlusIcon,
  CalendarDaysIcon,
  UsersIcon,
  ArrowRightIcon,
  Settings,
} from 'lucide-react';
import Logo from '../SiteBoard_Logo';
import GlobeIcon from '../icons/globeIcon';
import AddIcon from '../icons/addIcon';
import GantChartIcon from '../icons/gantChartIcon';

// test cases
const testScrollPanes = false; // add a bunch of copies to scrollable area to see what they look like when full of data
const testNoProjects = false; // show the components as if there were no data to populate it
const testCanCreateProjects = false; // show the component as if the user was an account manager
const testCannotCreateProjects = false; // show the component as if the user was not an acount manager

const Projects = ({ _projects, _subscriptions, _userId, _prefersProjectsListView, siteboardAction }) => {
  const [viewMode, setViewMode] = useState(_prefersProjectsListView ? 'list' : 'grid');
  const [is2xl, setIs2xl] = useState(() => typeof window !== 'undefined' ? window.matchMedia('(min-width: 96rem)').matches : false);
  
  // detect when the screen size is above or below tailwind 2xl
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 96rem)');
  
    const handleChange = () => {
      setIs2xl(mediaQuery.matches);
    };
  
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
  
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const projects = [];
  for(const key in _projects) {
    for(let i = 0; i < (testScrollPanes ? 20 : 1) && !testNoProjects; i++) {
      projects.push(_projects[key]);
    }
  }
  projects.sort((one, two) => two.modifiedTimestamp - one.modifiedTimestamp);

  let userCanCreateProjects = false;
  for(const subscriptionId in _subscriptions) {
    if(SiteboardApiValidator[ADD_PROJECT].isUserIdAuthorized(_userId, _subscriptions[subscriptionId], null)) {
      userCanCreateProjects = true;
      break;
    }
  }
  if(testCanCreateProjects) userCanCreateProjects = true;
  if(testCannotCreateProjects) userCanCreateProjects = false;

  return (
    <div className={'flex flex-col w-full h-full overflow-y-auto'}>
      {/* Top bar */}
      <div className={'flex min-h-[61px] bg-black/90'}>
        <div className='flex justify-end grow'>
          <div className='flex grow text-white justify-center items-center gap-x-[9px]'>
            <GlobeIcon size='1.5em' color='#fff168' />
            <p className='text-[1.4em] text-[#fff] font-normal'>Projects</p>
          </div>
        </div>
      </div>

      {/* Main area */}
      <div className='grow flex flex-col p-4 bg-[#1e1e1e] gap-6'>

        {/* Empty projects state */}
        {projects.length === 0 && userCanCreateProjects && (
          <div className="
            flex grow items-center justify-center
            px-4 py-12
            animate-in fade-in zoom-in-95 duration-500
          ">
            <div className="
              relative flex w-full max-w-[520px]
              flex-col items-center
              overflow-hidden rounded-2xl
              border border-white/10
              bg-[#222]
              px-6 py-10 sm:px-10 sm:py-12
              text-center text-white
              shadow-[0_20px_60px_rgba(0,0,0,0.2)]
            ">

              {/* Background ambient glow */}
              <div className="
                pointer-events-none absolute
                -top-28 left-1/2
                h-64 w-64 -translate-x-1/2
                rounded-full bg-[#471cff]/15
                blur-[80px]
              " />

              {/* Decorative top accent */}
              <div className="
                absolute inset-x-0 top-0 h-[2px]
                bg-gradient-to-r
                from-transparent via-[#471cff]
                to-transparent
              " />

              {/* Animated illustration */}
              <div className="
                relative mb-8 flex h-32 w-32
                items-center justify-center
              ">

                {/* Outer orbit */}
                <div className="
                  absolute inset-0 rounded-full
                  border border-dashed border-[#8d72ff]/30
                  animate-[spin_24s_linear_infinite]
                  motion-reduce:animate-none
                " />

                {/* Inner orbit */}
                <div className="
                  absolute inset-4 rounded-full
                  border border-[#8d72ff]/15
                " />

                {/* Floating icon container */}
                <div className="
                  relative flex h-20 w-20
                  items-center justify-center
                  rounded-2xl
                  border border-[#8d72ff]/30
                  bg-gradient-to-br
                  from-[#471cff]/30 to-[#471cff]/10
                  shadow-[0_0_45px_rgba(71,28,255,0.18)]
                  animate-[float_4s_ease-in-out_infinite]
                  motion-reduce:animate-none
                ">
                  <FolderPlusIcon
                    size={38}
                    strokeWidth={1.5}
                    className="text-[#ad9aff]"
                  />
                </div>

                {/* Orbiting decorative icons */}
                <div className="
                  absolute -right-2 top-3
                  flex h-9 w-9 items-center justify-center
                  rounded-xl border border-white/10
                  bg-[#303030]
                  shadow-lg
                  animate-[float_3.5s_ease-in-out_infinite]
                  motion-reduce:animate-none
                ">
                  <CalendarDaysIcon
                    size={17}
                    className="text-[#f9a928]"
                  />
                </div>

                <div className="
                  absolute -bottom-1 left-0
                  flex h-9 w-9 items-center justify-center
                  rounded-xl border border-white/10
                  bg-[#303030]
                  shadow-lg
                  animate-[float_4.5s_ease-in-out_infinite]
                  motion-reduce:animate-none
                ">
                  <UsersIcon
                    size={17}
                    className="text-[#ad9aff]"
                  />
                </div>

              </div>

              {/* Eyebrow */}
              <div className="
                mb-3 flex items-center gap-2
                text-xs font-bold uppercase
                tracking-[0.16em] text-[#f9a928]
              ">
                Your Workspace Awaits
              </div>

              {/* Heading */}
              <h2 className="
                mb-3 text-2xl sm:text-[1.8rem]
                font-bold tracking-tight
              ">
                Let's Create Your First Project
              </h2>

              {/* Description */}
              <p className="
                mb-7 max-w-[390px]
                text-sm leading-7 text-white/60
              ">
                Every great project starts with a plan.
                Create your first project to build 3-week
                schedules, organize your work, and
                collaborate with your team.
              </p>

              {/* Feature highlights */}
              <div className="
                mb-8 flex flex-wrap
                items-center justify-center
                gap-x-5 gap-y-3
                text-xs font-medium text-white/50
              ">
                <div className="flex items-center gap-2">
                  <CalendarDaysIcon
                    size={15}
                    className="text-[#f9a928]"
                  />
                  3-Week Schedules
                </div>

                <div className="flex items-center gap-2">
                  <UsersIcon
                    size={15}
                    className="text-[#f9a928]"
                  />
                  Team Collaboration
                </div>
              </div>

              {/* Primary action */}
              <div className="w-full max-w-[260px]">
                <PushButton
                  textColor="white"
                  bgColor="#471cff"
                  hoverBgColor="#603aff"
                  onClick={() => {
                    siteboardAction(SET_NEW_PROJECT_DIALOGUE_SHOWING, { isOpen: true });
                  }}
                >
                  <div className="
                    group flex w-full items-center
                    justify-center gap-3
                    py-1 font-semibold
                  ">
                    <FolderPlusIcon size={19} />

                    <span>Create Project</span>

                    <ArrowRightIcon
                      size={17}
                      className="
                        transition-transform duration-300
                        group-hover:translate-x-1
                      "
                    />
                  </div>
                </PushButton>
              </div>

              {/* Reassurance */}
              <p className="
                mt-5 text-xs text-white/35
              ">
                Get started in just a few clicks
              </p>

            </div>
          </div>
        )}

        {/* Non-empty projects - Create project and grid toggle */}
        {(is2xl || (projects.length > 0 && userCanCreateProjects)) &&
          <div className='flex items-center justify-between'>
            <div>
              {is2xl && 
                <GridListToggle 
                  value={viewMode} 
                  onChange={(viewType) => {
                    setViewMode(viewType);
                    siteboardAction(SET_PREFERS_PROJECTS_LIST_VIEW, { prefersProjectsListView: viewType === 'list' });
                  }} 
                />
              }
            </div>
            <div>
              {(projects.length > 0 && userCanCreateProjects) && 
                <PushButton 
                  isInline={true}
                  textColor={'white'}
                  bgColor={'#471cff'}
                  hoverBgColor="#603aff"
                  onClick={() => { 
                    siteboardAction(SET_NEW_PROJECT_DIALOGUE_SHOWING, { isOpen: true });
                  }}
                >
                  <div className='flex items-center gap-x-[6px]'>
                    <AddIcon color='#fff' />
                    <div>Create Project</div>
                  </div>
                </PushButton>
              }
            </div>
          </div>
        }

        {/* Project list */}
        {(projects.length > 0) && 
          <div className='grow basis-0'>
            {(viewMode === 'list' && is2xl) 
               ? 
                 <div className='text-left hidden 2xl:block'>
                   <SortableTable 
                     columns={[
                       { 
                         key: 'name',
                         label: "Project Name",
                         width:'30%',
                         render: (value, row) => <div className='font-semibold'>{value}</div>,
                       },
                       { 
                         key: 'jobNumber',
                         label: "Job#",
                         width:'7%',
                         render: (value, row) => value,
                       },
                       { 
                         key: 'address',
                         label: "Address",
                         width:'25%',
                         render: (value, row) => value,
                       },
                       { 
                         key: 'beginDate',
                         label: "Start",
                         width:'8%',
                         render: (value, row) => value,
                         sortValue: (value, row) => DateStringMMDDYYYY.toDateObject(value).getTime(),
                       },
                       { 
                         key: 'superintendent',
                         label: "Superintendent",
                         width:'14%', 
                         render: (value, row) => value,
                       },
                       { 
                         key: 'memberCount',
                         label: "Team", 
                         width:'5%', 
                         render: (value, row) => keyCountInObject(row.members) + keyCountInObject(row.pendingMembers),
                         sortValue: (value, row) => keyCountInObject(row.members) + keyCountInObject(row.pendingMembers),
                       },
                       { 
                         key: 'distributionCount',
                         label: "Distribution", 
                         width:'5%', 
                         render: (value, row) => row.contactCount,
                         sortValue: (value, row) => row.contactCount,
                       },
                       { 
                         key: null,
                         label: "",
                         width:'3%', 
                         render: (value, row) => 
                           <button 
                             className='flex justify-center group/settings'
                             title='Schedule'
                             onClick={() => { siteboardAction(SET_PATH, { path: `projectId#${row.id}#schedule` }); }}
                           >
                             <div className='relative'>
                               <div className='absolute top-0 left-0 opacity-0 group-hover/settings:opacity-100 transition-all duration-300'>
                                 <GantChartIcon size='26' color='#8060ff' />
                               </div>
                               <div className='opacity-100 group-hover/settings:opacity-0 transition-all duration-300'>
                                 <GantChartIcon size='26' color='#bbb' />
                               </div>
                             </div>
                           </button>,
                       },
                       { 
                         key: null,
                         label: "",
                         width:'3%', 
                         render: (value, row) => 
                           <button 
                             className='flex justify-center'
                             title='Settings'
                             onClick={() => { siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: row.id }); }}
                           >
                             <Settings size='24' className='text-white/70 hover:text-white hover:animate-[slow-step-spin_8s_ease-in-out_infinite] motion-reduce:animate-none' />
                           </button>,
                       },
                     ]}
                     data={projects}
                   />
                 </div>
               : 
                 <div className='grid grid-cols-1 min-[500px]:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 text-center'>
                   {projects.map((project, index) => (
                     <ProjectTile key={index} project={project} />
                   ))}
                 </div>
            }
          </div>
        }
      </div>
      <PoweredByBar />
    </div>
  );
}

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _userId: state.localPersistent.userId,
  _projects: state.serverSynced.projects,
  _subscriptions: state.serverSynced.subscriptions,
  _prefersProjectsListView: state.localPersistent.prefersProjectsListView,
}};
export default connect(mapStateToProps, {siteboardAction})(Projects);
